import { useEffect, useRef, useState } from 'react';
import { isLanguage, useLanguage, type Language } from '../i18n';

/**
 * The customer display: a second window (usually on a second, customer-facing monitor) that
 * mirrors the bill as it is rung up and shows the UPI QR when it is time to pay.
 *
 * The till and the display talk over a same-origin BroadcastChannel. The display only LISTENS:
 * it never calls the API and needs no sign-in, so a customer-facing screen holds no session a
 * passer-by could use. When a display opens it says hello and the till answers with what is on
 * the screen right now, so a display opened mid-bill catches up at once.
 */

const CHANNEL = 'sp.customer-display';

export interface DisplayLine {
  name: string;
  /** "2 × ₹27.00", "1.250 kg × ₹40.00" */
  detail: string;
  amountPaise: number;
  savedPaise: number;
}

interface DisplayBase {
  storeName: string;
  /** First name only: this screen faces the queue. */
  customerFirstName: string | null;
}

export type DisplayState =
  | (DisplayBase & { kind: 'idle' })
  | (DisplayBase & { kind: 'bill'; lines: DisplayLine[]; totalPaise: number; savedPaise: number; lastIndex: number })
  | (DisplayBase & {
      kind: 'pay';
      lines: DisplayLine[];
      totalPaise: number;
      savedPaise: number;
      lastIndex: number;
      /** The upi://pay link for the QR, when the store has a UPI ID and the bill takes UPI. */
      upiUri: string | null;
      upiPayee: string | null;
      upiAmountPaise: number;
    })
  | (DisplayBase & { kind: 'done'; invoiceNumber: string; totalPaise: number; changePaise: number; savedPaise: number });

type Message = { type: 'state'; state: DisplayState; language?: Language } | { type: 'hello' };

function openChannel(): BroadcastChannel | null {
  return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL);
}

/**
 * Till side: publish the current state whenever it changes, and repeat it to any display that
 * says hello. Publishing is cheap and silent when no display is open.
 */
export function useDisplayPublisher(state: DisplayState): void {
  const language = useLanguage((state) => state.language);
  const latest = useRef({ state, language });
  latest.current = { state, language };
  const channel = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    const opened = openChannel();
    channel.current = opened;
    if (!opened) return;
    opened.onmessage = (event: MessageEvent<Message>) => {
      if (event.data?.type === 'hello') opened.postMessage({ type: 'state', ...latest.current } satisfies Message);
    };
    return () => {
      opened.close();
      channel.current = null;
    };
  }, []);

  const serialised = JSON.stringify(state);
  useEffect(() => {
    channel.current?.postMessage({ type: 'state', state: JSON.parse(serialised) as DisplayState, language } satisfies Message);
  }, [serialised, language]);
}

/** Display side: the latest state the till published, or null until a till answers. */
export function useDisplayFeed(): DisplayState | null {
  const [state, setState] = useState<DisplayState | null>(null);
  useEffect(() => {
    const opened = openChannel();
    if (!opened) return;
    opened.onmessage = (event: MessageEvent<Message>) => {
      if (event.data?.type === 'state') {
        if (isLanguage(event.data.language)) useLanguage.getState().followTillLanguage(event.data.language);
        setState(event.data.state);
      }
    };
    opened.postMessage({ type: 'hello' } satisfies Message);
    return () => opened.close();
  }, []);
  return state;
}

/** Opens (or brings forward) the customer display window. */
export function openCustomerDisplay(): void {
  const display = window.open('/display', 'sp-customer-display', 'popup,width=1280,height=800');
  display?.focus();
}

export function firstName(name: string | null | undefined): string | null {
  const first = (name ?? '').trim().split(/\s+/)[0];
  return first ? first : null;
}
